import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import SavedPayslipHeader from './SavedPayslipHeader';

export const n = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = parseFloat(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

const sumAmounts = (list) => {
  const values = Array.isArray(list) ? list : [];
  return values.reduce((sum, item) => sum + n(item?.amount), 0);
};

const getBreakdown = (record) => record?.deduction_settings?.breakdown || {};

const isGeneratedLoanDeductionItem = (item, loanCycles = []) => {
  if (!item) return false;
  if (item?.is_loan_deduction === true) return true;
  const loanId = String(item?.loan_id || '').trim();
  const knownLoanIds = new Set((Array.isArray(loanCycles) ? loanCycles : []).map((cycle) => String(cycle?.loan_id || '').trim()).filter(Boolean));
  if (loanId && knownLoanIds.has(loanId)) return true;
  const knownLabels = new Set(
    (Array.isArray(loanCycles) ? loanCycles : [])
      .map((cycle) => String(cycle?.loan_label || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const title = String(item?.title || '').trim().toLowerCase();
  const description = String(item?.description || '').trim().toLowerCase();
  if (title.startsWith('loan - ')) {
    const rawLabel = title.slice('loan - '.length).trim();
    if (knownLabels.size === 0 || knownLabels.has(rawLabel)) return true;
  }
  if (description.startsWith('loan deduction for ')) {
    const rawLabel = description.slice('loan deduction for '.length).trim();
    if (knownLabels.size === 0 || knownLabels.has(rawLabel)) return true;
  }
  return false;
};

const mfLoanRowAmount = (row) => {
  if (!row) return 0;
  const v = row?.deducted_amount ?? row?.scheduled_amount ?? row?.deduct_per_cutoff ?? row?.amount;
  return n(v);
};

const mfLoanLabel = (loan, index) => {
  const baseLabel = String(
    loan?.loan_label
    || loan?.loan_subtype
    || loan?.title
    || loan?.description
    || ''
  ).trim();
  return baseLabel || `Loan ${index + 1}`;
};

export const normalizeMfLoanSummary = (loanSummary) => {
  const rows = Array.isArray(loanSummary?.rows) ? loanSummary.rows : [];
  const enabledRows = rows.filter((row) => {
    if (String(row?.cycle_status || '').toLowerCase() === 'skipped') return false;
    if (row?.isSelected === false) return false;
    return mfLoanRowAmount(row) > 0;
  });
  const loanFormTypes = enabledRows
    .map((row) => String(row?.loan_label || row?.loan_subtype || row?.title || '').trim())
    .filter(Boolean);
  const loanDeductionPerCutoffTotal = enabledRows.reduce((sum, row) => sum + mfLoanRowAmount(row), 0);
  return {
    rows: enabledRows,
    loanFormTypes,
    loanDeductionPerCutoffTotal
  };
};

const buildLoanSummaryFromRecord = (record) => {
  const cycles = Array.isArray(record?.deduction_settings?.loan_cycles) ? record.deduction_settings.loan_cycles : [];
  if (cycles.length === 0) return null;
  return normalizeMfLoanSummary({ rows: cycles });
};

export const buildMfAmountItems = (items, dailyRate) => {
  const minuteRate = n(dailyRate) / 8 / 60;
  return (Array.isArray(items) ? items : []).map((item) => {
    const unit = item?.unit || 'minutes';
    let amount = 0;
    if (unit === 'amount') amount = n(item?.amount);
    else if (unit === 'days') amount = n(item?.days) * n(dailyRate);
    else amount = minuteRate * n(item?.minutes);
    return { ...item, amount };
  });
};

export const computeMfDisplay = (record) => {
  const breakdown = getBreakdown(record);
  const tilesIncome = record?.tiles?.income_breakdown || {};
  const tilesDed = record?.tiles?.deductions || {};
  const tilesNet = record?.tiles?.net_pay || {};
  const num = (v) => {
    if (v === undefined || v === null || Number.isNaN(v)) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const p = parseFloat(String(v).replace(/,/g, '').trim());
    return Number.isFinite(p) ? p : null;
  };
  const fromTiles = (tile, fallback) => {
    const t = num(tile);
    if (t !== null) return t;
    const f = num(fallback);
    return f !== null ? f : 0;
  };

  const mfAmount = num(tilesIncome?.half_month_salary)
    ?? num(breakdown?.income?.half_month_salary)
    ?? n(record?.half_month_salary)
    ?? 0;
  const absenceDays = num(tilesIncome?.days_absent) ?? n(record?.days_absent);
  const lateMinutes = num(tilesIncome?.minutes_late) ?? n(record?.minutes_late);
  const undertimeMinutes = num(tilesIncome?.minutes_undertime) ?? n(record?.minutes_undertime);
  const absenceDeduction = fromTiles(tilesIncome?.absence, breakdown?.deductions?.absence || record?.absence_deduction);
  const tardinessDeduction = fromTiles(tilesIncome?.tardiness, breakdown?.deductions?.tardiness || record?.tardiness_deduction);
  const undertimeDeduction = fromTiles(tilesIncome?.undertime, breakdown?.deductions?.undertime || record?.undertime_deduction);

  const grossMfTileNum = num(tilesIncome?.gross_salary) ?? num(tilesNet?.gross_salary);
  const grossMf = grossMfTileNum !== null
    ? grossMfTileNum
    : (num(record?.gross_salary) ?? num(breakdown?.summary?.basic_salary) ?? n(record?.basic_salary));

  const persistedLoanSummary = buildLoanSummaryFromRecord(record);
  const effectiveLoanSummary = normalizeMfLoanSummary(persistedLoanSummary || { rows: [] });
  const loanCycles = Array.isArray(record?.deduction_settings?.loan_cycles) ? record.deduction_settings.loan_cycles : [];
  const additionalIncome = Array.isArray(record?.additional_income) ? record.additional_income : [];
  const additionalDeductions = Array.isArray(record?.additional_deductions) ? record.additional_deductions : [];
  const incomeItems = Array.isArray(breakdown?.income?.items)
    ? breakdown.income.items
    : additionalIncome;
  const deductionItems = additionalDeductions.filter((item) => !isGeneratedLoanDeductionItem(item, loanCycles));

  const mfSss = fromTiles(tilesDed?.sss, 0);
  const mfHdmf = fromTiles(tilesDed?.hdmf, 0);
  const mfPhilhealth = fromTiles(tilesDed?.philhealth, 0);
  const mfTax = fromTiles(tilesDed?.withholding_tax, 0);

  const tileTotDed = num(tilesDed?.total_deductions);
  const tileNetTotDed = num(tilesNet?.total_deductions);
  const additionalDeductionsTotal = tileTotDed !== null
    ? tileTotDed
    : (tileNetTotDed !== null ? tileNetTotDed : sumAmounts(deductionItems));

  const rawTileNetPay = num(tilesNet?.net_pay);
  const netMf = rawTileNetPay !== null
    ? rawTileNetPay
    : (num(record?.net_pay) ?? 0);

  return {
    mfAmount,
    absenceDays,
    lateMinutes,
    undertimeMinutes,
    absenceDeduction,
    tardinessDeduction,
    undertimeDeduction,
    grossMf,
    incomeItems,
    deductionItems,
    additionalDeductionsTotal,
    loanRows: effectiveLoanSummary.rows,
    loanDeduction: Number(effectiveLoanSummary?.loanDeductionPerCutoffTotal) || 0,
    loanSummary: effectiveLoanSummary,
    netMf,
    mfSss,
    mfHdmf,
    mfPhilhealth,
    mfTax
  };
};

export const formatMfCurrency = (value) => `₱${n(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const getMfItemLabel = (item) => String(item?.title || item?.description || '').trim() || 'Item';

const SavedMfPayslipView = ({
  record,
  className = 'bg-white p-8 border-2 border-gray-300 rounded-lg'
}) => {
  const safeRecord = record || {};
  const display = computeMfDisplay(safeRecord);
  const holidays = Array.isArray(safeRecord?.holidays) ? safeRecord.holidays : [];
  const mfLoanRows = Array.isArray(display.loanRows) ? display.loanRows : [];
  const mfLoansToShow = mfLoanRows.filter((r) => mfLoanRowAmount(r) > 0);
  const hasLoanDetail = mfLoansToShow.length > 0;
  const showSss = n(display.mfSss) > 0;
  const showHdmf = n(display.mfHdmf) > 0;
  const showPhilhealth = n(display.mfPhilhealth) > 0;
  const showTax = n(display.mfTax) > 0;

  return (
    <div className={className}>
      <SavedPayslipHeader record={safeRecord} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-green-800">Income Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {holidays.length > 0 ? (
              <div className="border-b pb-2 mb-2">
                <p className="text-xs font-semibold text-gray-700 mb-1">Holidays:</p>
                <div className="space-y-1">
                  {holidays.map((holiday, idx) => (
                    <div key={`${safeRecord?.id || 'mf'}_holiday_${idx}`} className="text-xs text-gray-700">
                      {String(holiday?.date || '')}{holiday?.name ? ` — ${String(holiday.name)}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex justify-between">
              <span>MF Amount:</span>
              <span className="font-medium">{formatMfCurrency(display.mfAmount)}</span>
            </div>

            <div className="border-t pt-2">
              <p className="text-xs font-semibold text-red-700 mb-1">Less:</p>
              <div className="flex justify-between pl-2 text-xs">
                <span>Absence ({display.absenceDays} days):</span>
                <span className="text-red-600">-{formatMfCurrency(display.absenceDeduction)}</span>
              </div>
              <div className="flex justify-between pl-2 text-xs">
                <span>Tardiness ({display.lateMinutes} mins):</span>
                <span className="text-red-600">-{formatMfCurrency(display.tardinessDeduction)}</span>
              </div>
              <div className="flex justify-between pl-2 text-xs">
                <span>Undertime ({display.undertimeMinutes} mins):</span>
                <span className="text-red-600">-{formatMfCurrency(display.undertimeDeduction)}</span>
              </div>
            </div>

            {display.incomeItems.length > 0 ? (
              <div className="border-t pt-2">
                <p className="text-xs font-semibold text-green-700 mb-1">Additional Income:</p>
                {display.incomeItems.map((income, idx) => (
                  <div key={`${safeRecord?.id || 'mf'}_income_${idx}`} className="flex justify-between pl-2 text-xs">
                    <span>{getMfItemLabel(income)}:</span>
                    <span className="text-green-600">+{formatMfCurrency(income?.amount)}</span>
                  </div>
                ))}
              </div>
            ) : null}

            {display.deductionItems.length > 0 ? (
              <div className="border-t pt-2">
                <p className="text-xs font-semibold text-red-700 mb-1">Additional Deductions:</p>
                {display.deductionItems.map((deduction, idx) => (
                  <div key={`${safeRecord?.id || 'mf'}_deduction_${idx}`} className="flex justify-between pl-2 text-xs">
                    <span>{getMfItemLabel(deduction)}:</span>
                    <span className="text-red-600">-{formatMfCurrency(deduction?.amount)}</span>
                  </div>
                ))}
              </div>
            ) : null}

            {(showSss || showHdmf || showPhilhealth || showTax) ? (
              <div className="border-t pt-2 mt-2">
                <p className="text-xs font-semibold text-red-700 mb-1">Government Deductions:</p>
                {showSss ? (
                  <div className="flex justify-between pl-2 text-xs">
                    <span>SSS:</span>
                    <span className="text-red-600">-{formatMfCurrency(display.mfSss)}</span>
                  </div>
                ) : null}
                {showHdmf ? (
                  <div className="flex justify-between pl-2 text-xs">
                    <span>HDMF:</span>
                    <span className="text-red-600">-{formatMfCurrency(display.mfHdmf)}</span>
                  </div>
                ) : null}
                {showPhilhealth ? (
                  <div className="flex justify-between pl-2 text-xs">
                    <span>PhilHealth:</span>
                    <span className="text-red-600">-{formatMfCurrency(display.mfPhilhealth)}</span>
                  </div>
                ) : null}
                {showTax ? (
                  <div className="flex justify-between pl-2 text-xs">
                    <span>Withholding Tax:</span>
                    <span className="text-red-600">-{formatMfCurrency(display.mfTax)}</span>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="flex justify-between font-bold pt-2 border-t-2 text-base text-green-700">
              <span>Gross MF:</span>
              <span>{formatMfCurrency(display.grossMf)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300 border-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-blue-800">Net Pay</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Gross MF:</span>
                <span className="font-medium">{formatMfCurrency(display.grossMf)}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Deductions:</span>
                <span className="font-medium text-red-600">{formatMfCurrency(display.additionalDeductionsTotal)}</span>
              </div>
              {hasLoanDetail ? (
                mfLoansToShow.map((loan, idx) => {
                  const rowAmount = mfLoanRowAmount(loan);
                  return (
                    <div
                      key={`${safeRecord?.id || 'mf'}_loan_${loan?.loan_id || loan?.id || idx}`}
                      className="flex justify-between"
                    >
                      <span className="font-medium text-slate-700">{mfLoanLabel(loan, idx)}</span>
                      <span className="font-medium text-red-600">-{formatMfCurrency(rowAmount)}</span>
                    </div>
                  );
                })
              ) : null}
            </div>
            <div className="border-t-2 border-blue-300 pt-3">
              <div className="flex justify-between items-center">
                <span className="font-bold text-blue-900 text-lg">NET MF:</span>
                <span className="font-bold text-green-700 text-2xl">{formatMfCurrency(display.netMf)}</span>
              </div>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <p className="text-xs text-blue-700 text-center">
                MF calculations are independent from Payroll.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SavedMfPayslipView;
